/**
 * @fileoverview React hook for Stripe Connect operations
 * @module stripe-connect/hooks/useStripeConnect
 *
 * Implements AbortController pattern for request cancellation on unmount.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { getStripeConnectService } from '../services/StripeConnectService';
import type {
  StripeAccountStatusResponse,
  StripeProduct,
  StripePrice,
  StripeCoupon,
  StripePaymentMethodDomain,
} from '../types';

/**
 * Check if error is an abort error (request was cancelled)
 */
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Price creation data
 */
export interface CreatePriceData {
  productId: string;
  unitAmount: number;
  currency: string;
  type: 'one_time' | 'recurring';
  recurring?: {
    interval: 'day' | 'week' | 'month' | 'year';
    intervalCount: number;
  };
  nickname?: string;
  /**
   * Multi-currency support: prices in additional currencies
   * Key is currency code (e.g., 'eur', 'gbp'), value contains unitAmount in cents
   * @see https://docs.stripe.com/payments/checkout/localize-prices/manual-currency-prices
   */
  currencyOptions?: Record<string, { unitAmount: number }>;
}

/**
 * Hook return type - supports tracking both test and live account statuses
 */
export interface UseStripeConnectResult {
  /** Test account status */
  testStatus: StripeAccountStatusResponse | null;
  /** Live account status */
  liveStatus: StripeAccountStatusResponse | null;
  /** Loading state (either mode) */
  loading: boolean;
  /** Error message */
  error: string | null;

  /** Refresh test account status */
  refreshTestStatus: () => Promise<void>;
  /** Refresh live account status */
  refreshLiveStatus: () => Promise<void>;
  /** Refresh both statuses */
  refreshAll: () => Promise<void>;

  /** Disconnect test account */
  disconnectTest: () => Promise<void>;
  /** Disconnect live account */
  disconnectLive: () => Promise<void>;

  /** List products for a mode */
  products: StripeProduct[];
  /** Loading products state */
  loadingProducts: boolean;
  /** Fetch products for a specific mode */
  fetchProducts: (mode: 'test' | 'live') => Promise<void>;
  /** List prices for a product in a specific mode */
  fetchPrices: (productId: string, mode: 'test' | 'live') => Promise<StripePrice[]>;
  /**
   * Create a new product on connected account. `options.confirmLive: true` is
   * required by the server guard for a LIVE-mode call (P7); omit for test.
   */
  createProduct: (
    name: string,
    description: string | undefined,
    mode: 'test' | 'live',
    options?: { confirmLive?: boolean }
  ) => Promise<StripeProduct>;
  /**
   * Create a new price on connected account. `options.confirmLive: true` is
   * required by the server guard for a LIVE-mode call (P7); omit for test.
   */
  createPrice: (
    data: CreatePriceData,
    mode: 'test' | 'live',
    options?: { confirmLive?: boolean }
  ) => Promise<StripePrice>;

  /** List coupons for a mode */
  coupons: StripeCoupon[];
  /** Loading coupons state */
  loadingCoupons: boolean;
  /** Fetch coupons for a specific mode */
  fetchCoupons: (mode: 'test' | 'live') => Promise<void>;

  /** Apple Pay / payment method domains by Stripe mode */
  paymentMethodDomains: Record<'test' | 'live', StripePaymentMethodDomain[]>;
  /** Loading state for payment method domains by Stripe mode */
  loadingPaymentMethodDomains: Record<'test' | 'live', boolean>;
  /** Fetch payment method domains for a specific mode */
  fetchPaymentMethodDomains: (mode: 'test' | 'live') => Promise<void>;
  /** Register or re-validate a payment method domain */
  registerPaymentMethodDomain: (
    mode: 'test' | 'live',
    domainName: string
  ) => Promise<StripePaymentMethodDomain>;
}

/**
 * React hook for managing Stripe Connect operations
 *
 * @param projectId - Project ID
 * @returns Stripe Connect operations and state
 *
 * @example
 * ```tsx
 * const { status, loading, connect, disconnect } = useStripeConnect(projectId);
 *
 * if (status?.connected) {
 *   return <StripeAccountStatus status={status} onDisconnect={disconnect} />;
 * }
 *
 * return <Button onClick={connect}>Connect Stripe</Button>;
 * ```
 */
export function useStripeConnect(projectId: string): UseStripeConnectResult {
  const [testStatus, setTestStatus] = useState<StripeAccountStatusResponse | null>(null);
  const [liveStatus, setLiveStatus] = useState<StripeAccountStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [coupons, setCoupons] = useState<StripeCoupon[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [paymentMethodDomains, setPaymentMethodDomains] = useState<Record<'test' | 'live', StripePaymentMethodDomain[]>>({
    test: [],
    live: [],
  });
  const [loadingPaymentMethodDomains, setLoadingPaymentMethodDomains] = useState<Record<'test' | 'live', boolean>>({
    test: false,
    live: false,
  });

  const service = getStripeConnectService();

  // AbortController refs for request cancellation
  const testStatusAbortRef = useRef<AbortController | null>(null);
  const liveStatusAbortRef = useRef<AbortController | null>(null);
  const productsAbortRef = useRef<AbortController | null>(null);
  const pricesAbortRef = useRef<AbortController | null>(null);
  const couponsAbortRef = useRef<AbortController | null>(null);
  const paymentMethodDomainsAbortRef = useRef<Record<'test' | 'live', AbortController | null>>({
    test: null,
    live: null,
  });
  const mountedRef = useRef(true);
  const statusRefreshGenerationRef = useRef(0);

  /**
   * Fetch test account status
   */
  const refreshTestStatus = useCallback(
    async () => {
      if (!projectId) return;

      // Cancel any in-flight test status request
      testStatusAbortRef.current?.abort();
      testStatusAbortRef.current = new AbortController();

      try {
        const response = await service.getAccountStatus(
          projectId,
          'test',
          testStatusAbortRef.current.signal
        );
        if (mountedRef.current) {
          setTestStatus(response);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch test account status');
        }
      }
    },
    [projectId, service]
  );

  /**
   * Fetch live account status
   */
  const refreshLiveStatus = useCallback(
    async () => {
      if (!projectId) return;

      // Cancel any in-flight live status request
      liveStatusAbortRef.current?.abort();
      liveStatusAbortRef.current = new AbortController();

      try {
        const response = await service.getAccountStatus(
          projectId,
          'live',
          liveStatusAbortRef.current.signal
        );
        if (mountedRef.current) {
          setLiveStatus(response);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch live account status');
        }
      }
    },
    [projectId, service]
  );

  /**
   * Refresh both test and live statuses
   */
  const refreshAll = useCallback(
    async () => {
      if (!projectId) return;

      const generation = ++statusRefreshGenerationRef.current;
      setLoading(true);
      setError(null);

      try {
        await Promise.all([refreshTestStatus(), refreshLiveStatus()]);
      } finally {
        // An aborted earlier refresh must not settle the replacement request.
        if (mountedRef.current && generation === statusRefreshGenerationRef.current) {
          setLoading(false);
        }
      }
    },
    [projectId, refreshTestStatus, refreshLiveStatus]
  );

  /**
   * Disconnect test account
   */
  const disconnectTest = useCallback(async () => {
    if (!projectId) return;

    setError(null);

    try {
      await service.disconnectAccount(projectId, 'test');
      setTestStatus({ connected: false, mode: 'test' });
      setProducts([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect test account');
    }
  }, [projectId, service]);

  /**
   * Disconnect live account
   */
  const disconnectLive = useCallback(async () => {
    if (!projectId) return;

    setError(null);

    try {
      await service.disconnectAccount(projectId, 'live');
      setLiveStatus({ connected: false, mode: 'live' });
      setProducts([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect live account');
    }
  }, [projectId, service]);

  /**
   * Fetch products from connected account
   * @param mode - Mode to fetch products for (required)
   */
  const fetchProducts = useCallback(
    async (mode: 'test' | 'live') => {
      if (!projectId) return;

      // Cancel any in-flight products request
      productsAbortRef.current?.abort();
      productsAbortRef.current = new AbortController();

      setLoadingProducts(true);
      setError(null);

      try {
        const productList = await service.listProducts(
          projectId,
          mode,
          productsAbortRef.current.signal
        );
        if (mountedRef.current) {
          setProducts(productList);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch products');
        }
      } finally {
        if (mountedRef.current) {
          setLoadingProducts(false);
        }
      }
    },
    [projectId, service]
  );

  /**
   * Fetch prices for a product
   * @param productId - Stripe Product ID
   * @param mode - Mode to fetch prices for (required)
   */
  const fetchPrices = useCallback(
    async (productId: string, mode: 'test' | 'live'): Promise<StripePrice[]> => {
      if (!projectId) return [];

      // Cancel any in-flight prices request
      pricesAbortRef.current?.abort();
      pricesAbortRef.current = new AbortController();

      try {
        return await service.listPrices(
          projectId,
          productId,
          mode,
          pricesAbortRef.current.signal
        );
      } catch (err) {
        if (isAbortError(err)) return [];
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch prices');
        }
        return [];
      }
    },
    [projectId, service]
  );

  /**
   * Fetch coupons from connected account
   * @param mode - Mode to fetch coupons for (required)
   */
  const fetchCoupons = useCallback(
    async (mode: 'test' | 'live') => {
      if (!projectId) return;

      // Cancel any in-flight coupons request
      couponsAbortRef.current?.abort();
      couponsAbortRef.current = new AbortController();

      setLoadingCoupons(true);
      setError(null);

      try {
        const couponList = await service.listCoupons(
          projectId,
          mode,
          couponsAbortRef.current.signal
        );
        if (mountedRef.current) {
          setCoupons(couponList);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch coupons');
        }
      } finally {
        if (mountedRef.current) {
          setLoadingCoupons(false);
        }
      }
    },
    [projectId, service]
  );

  /**
   * Fetch payment method domains for Apple Pay / wallet display.
   */
  const fetchPaymentMethodDomains = useCallback(
    async (mode: 'test' | 'live') => {
      if (!projectId) return;

      paymentMethodDomainsAbortRef.current[mode]?.abort();
      paymentMethodDomainsAbortRef.current[mode] = new AbortController();

      setLoadingPaymentMethodDomains((current) => ({ ...current, [mode]: true }));
      setError(null);

      try {
        const domains = await service.listPaymentMethodDomains(
          projectId,
          mode,
          paymentMethodDomainsAbortRef.current[mode]?.signal
        );
        if (mountedRef.current) {
          setPaymentMethodDomains((current) => ({ ...current, [mode]: domains }));
        }
      } catch (err) {
        if (isAbortError(err)) return;
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch payment method domains');
        }
      } finally {
        if (mountedRef.current) {
          setLoadingPaymentMethodDomains((current) => ({ ...current, [mode]: false }));
        }
      }
    },
    [projectId, service]
  );

  /**
   * Register or re-validate a domain for Apple Pay.
   */
  const registerPaymentMethodDomain = useCallback(
    async (mode: 'test' | 'live', domainName: string): Promise<StripePaymentMethodDomain> => {
      if (!projectId) throw new Error('Project ID is required');

      setError(null);

      try {
        const domain = await service.registerPaymentMethodDomain(projectId, mode, domainName);
        await fetchPaymentMethodDomains(mode);
        return domain;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to register payment method domain';
        setError(message);
        throw new Error(message);
      }
    },
    [projectId, service, fetchPaymentMethodDomains]
  );

  /**
   * Create a product on connected account
   * @param name - Product name
   * @param description - Optional product description
   * @param mode - Mode to create product in (required)
   */
  const createProduct = useCallback(
    async (
      name: string,
      description: string | undefined,
      mode: 'test' | 'live',
      options?: { confirmLive?: boolean }
    ): Promise<StripeProduct> => {
      if (!projectId) throw new Error('Project ID is required');

      setError(null);

      try {
        const product = await service.createProduct(projectId, name, description, mode, options);
        // Refresh products list to include the new product
        await fetchProducts(mode);
        return product;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create product';
        setError(message);
        throw new Error(message);
      }
    },
    [projectId, service, fetchProducts]
  );

  /**
   * Create a price on connected account
   * @param data - Price creation data
   * @param mode - Mode to create price in (required)
   * @param options - `confirmLive: true` required by the server guard for a
   *   LIVE-mode call (P7); omit for test.
   */
  const createPrice = useCallback(
    async (data: CreatePriceData, mode: 'test' | 'live', options?: { confirmLive?: boolean }): Promise<StripePrice> => {
      if (!projectId) throw new Error('Project ID is required');

      setError(null);

      try {
        const price = await service.createPrice(projectId, data, mode, options);
        return price;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create price';
        setError(message);
        throw new Error(message);
      }
    },
    [projectId, service]
  );

  // Fetch both statuses on mount and cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    refreshAll();

    // Cleanup: abort all in-flight requests and mark as unmounted
    return () => {
      mountedRef.current = false;
      testStatusAbortRef.current?.abort();
      liveStatusAbortRef.current?.abort();
      productsAbortRef.current?.abort();
      pricesAbortRef.current?.abort();
      couponsAbortRef.current?.abort();
      paymentMethodDomainsAbortRef.current.test?.abort();
      paymentMethodDomainsAbortRef.current.live?.abort();
    };
  }, [refreshAll]);

  return {
    testStatus,
    liveStatus,
    loading,
    error,
    refreshTestStatus,
    refreshLiveStatus,
    refreshAll,
    disconnectTest,
    disconnectLive,
    products,
    loadingProducts,
    fetchProducts,
    fetchPrices,
    createProduct,
    createPrice,
    coupons,
    loadingCoupons,
    fetchCoupons,
    paymentMethodDomains,
    loadingPaymentMethodDomains,
    fetchPaymentMethodDomains,
    registerPaymentMethodDomain,
  };
}
